import { all, call, put, takeLatest } from "redux-saga/effects";
import * as actions from "../slices/cgmSlice";
import CgmApis from "src/api/cgm";
import { withCgmDailyLogsTimezone } from "src/helpers/cgm";
import { constants } from "src/constants/common";
import type {
  CgmDailyLog,
  CgmCalendarData,
  CgmStatusData,
} from "src/types/cgm.types";

function* getDailyLogsSaga({
  payload,
}: {
  type: string;
  payload: { startDate: string };
}) {
  try {
    const response: unknown = yield call(
      CgmApis.getCGMDailyLogs,
      withCgmDailyLogsTimezone({ startDate: payload.startDate })
    );
    const res = response as { code?: number; data?: unknown[] };
    if (Number(res?.code) === -1) {
      throw new Error("Session Expire");
    }
    if (Number(res?.code) === 0) {
      throw new Error(
        (res as { message?: string }).message ?? "Request failed"
      );
    }
    const data: CgmDailyLog | null =
      Array.isArray(res?.data) && res.data.length > 0
        ? (res.data[0] as CgmDailyLog)
        : null;
    yield put(actions.getDailyLogsSuccess({ data }));
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG;
    yield put(actions.getDailyLogsError(message));
  }
}

function* getCGMCalendarSaga({
  payload,
}: {
  type: string;
  payload: { month: number; year: number };
}) {
  try {
    const response: unknown = yield call(CgmApis.getCGMCalendar, {
      month: payload.month,
      year: payload.year,
    });
    const res = response as { code?: number; data?: unknown[] };
    if (Number(res?.code) === -1) throw new Error("Session Expire");
    if (Number(res?.code) === 0)
      throw new Error(
        (res as { message?: string }).message ?? "Request failed"
      );
    const data: CgmCalendarData | null =
      Array.isArray(res?.data) && res.data.length > 0
        ? (res.data[0] as CgmCalendarData)
        : (res?.data as CgmCalendarData) ?? null;
    yield put(actions.getCGMCalendarSuccess({ data }));
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG;
    yield put(actions.getCGMCalendarError(message));
  }
}

function* getCGMCumulativeLogsSaga({
  payload,
}: {
  type: string;
  payload: { startDate: string; endDate: string };
}) {
  try {
    const response: unknown = yield call(CgmApis.getCGMCumulativeLogs, {
      startDate: payload.startDate,
      endDate: payload.endDate,
    });
    const res = response as { code?: number; data?: unknown[] };
    if (Number(res?.code) === -1) throw new Error("Session Expire");
    if (Number(res?.code) === 0)
      throw new Error(
        (res as { message?: string }).message ?? "Request failed"
      );
    const raw = res?.data;
    const data: Record<string, unknown> | null =
      Array.isArray(raw) && raw.length > 0
        ? (raw[0] as Record<string, unknown>)
        : raw && typeof raw === "object" && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : null;
    yield put(actions.getCGMCumulativeLogsSuccess({ data }));
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG;
    yield put(actions.getCGMCumulativeLogsError(message));
  }
}

function* getCgmStatusSaga() {
  try {
    const response: unknown = yield call(CgmApis.getCGMStatus);
    const res = response as { code?: number; data?: unknown };
    if (Number(res?.code) === -1) throw new Error("Session Expire");
    if (Number(res?.code) === 0)
      throw new Error(
        (res as { message?: string }).message ?? "Request failed"
      );
    yield put(actions.getCgmStatusSuccess({ data: res?.data as any }));
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG;
    yield put(actions.getCgmStatusError(message));
  }
}

export default function* cgmSaga() {
  yield all([
    takeLatest(actions.getDailyLogsRequest.type, getDailyLogsSaga),
    takeLatest(actions.getCgmStatusRequest.type, getCgmStatusSaga),
    takeLatest(actions.getCGMCalendarRequest.type, getCGMCalendarSaga),
    takeLatest(
      actions.getCGMCumulativeLogsRequest.type,
      getCGMCumulativeLogsSaga
    ),
  ]);
}
